import { CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAssistantPref } from '../../../../../data-access';
import './AIAssistantMealSettings.scss';
import { MealSettingItem } from './MealSettingItem';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { AppSwitch } from '../../../../common';

const mealTypes = ['breakfast', 'lunch', 'dinner'] as const;
const mealLabelKeys: Record<(typeof mealTypes)[number], string> = {
  breakfast: 'settings.aiAssistant.meals.breakfast',
  lunch: 'settings.aiAssistant.meals.lunch',
  dinner: 'settings.aiAssistant.meals.dinner',
};
const AIAssistantMealSettings = () => {
  const { t } = useTranslation();
  const { mealPref, updateMealPref, isPreferenceLoading } = useAssistantPref();
  const [isAutoScheduleEnabled, setisAutoSchedulerEnabled] = useState(
    !mealPref.disableAutoSchedule
  );
  const [excludePlanContents, setExcludePlanContents] = useState(
    mealPref.excludePlanContents ?? false
  );
  const [note, setNote] = useState(mealPref.note ?? '');

  const debouncedSaveNote = useMemo(
    () =>
      debounce((value: string) => {
        updateMealPref({ note: value });
      }, 2000),
    [updateMealPref]
  );

  useEffect(() => {
    return () => {
      debouncedSaveNote.cancel();
    };
  }, [debouncedSaveNote]);

  useEffect(() => {
    setisAutoSchedulerEnabled(!mealPref.disableAutoSchedule);
  }, [mealPref.disableAutoSchedule]);

  useEffect(() => {
    setExcludePlanContents(mealPref.excludePlanContents ?? false);
  }, [mealPref.excludePlanContents]);

  useEffect(() => {
    setNote(mealPref.note ?? '');
  }, [mealPref.note]);

  const handleToggleAutoSchedule = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setisAutoSchedulerEnabled(checked);
    updateMealPref({
      disableAutoSchedule: !checked,
    });
  };

  if (isPreferenceLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <CircularProgress size={16} />
      </Stack>
    );
  }

  return (
    <Stack gap={3} className="ai-assistant-meal-settings">
      <Stack gap={1}>
        <Typography className="ai-assistant-meal-settings__note-label" variant="subtitle1">
          {t('settings.common.note')}
        </Typography>
        <TextField
          className="ai-assistant-meal-settings__note"
          placeholder={t('settings.aiAssistant.mealNotePlaceholder')}
          value={note}
          onChange={(e) => {
            const value = e.target.value;
            setNote(value);
            debouncedSaveNote(value);
          }}
          multiline
          minRows={1}
          size="small"
          fullWidth
          variant="standard"
          InputProps={{ disableUnderline: true }}
        />
      </Stack>
      <div className="ai-assistant-meal-settings__item-content">
        <div className="ai-assistant-meal-settings__item-content__title">
          {t('settings.aiAssistant.includeMealPlanContents')}
        </div>
        <AppSwitch
          checked={!excludePlanContents}
          onChange={(_e, checked) => {
            setExcludePlanContents(!checked);
            updateMealPref({ excludePlanContents: !checked });
          }}
        />
      </div>
      {mealTypes.map((type) => {
        const meal = mealPref[type];
        if (!meal) return null;
        return (
          <Stack gap={1} key={type}>
            <Typography className="ai-assistant-meal-settings__item-title" variant="subtitle1">
              {t(mealLabelKeys[type])}
            </Typography>
            <MealSettingItem
              mealPreferenceItem={meal}
              mealType={type}
              onRecurringChange={(day) => {
                updateMealPref({
                  [type]: {
                    ...meal,
                    recurring: day,
                  },
                });
              }}
              onTimeChange={(start, end) => {
                updateMealPref({
                  [type]: {
                    ...meal,
                    earliestTime: start,
                    latestTime: end,
                  },
                });
              }}
              onDurationChange={(duration) => {
                updateMealPref({
                  [type]: {
                    ...meal,
                    durationMin: duration,
                  },
                });
              }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default AIAssistantMealSettings;
